import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { ChangeLog, Pagination, Response } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  PspPartnerItem,
  PspAccountsValidator,
  PspAccountItem,
  Balance,
  Document,
  PspIntegration,
  PaymentsSettings,
  ExternalAccount,
  IntegrationLog,
  ExternalAccountTopUpOption,
  PspAccountCategory,
  TransactionFeeSettings,
  PspPartnersLinkedClient,
} from '../../types';
import { pspApi } from '../services';
import { BankRoutes } from '../../../../routes';

export interface DocumentFormValues {
  documentPath?: string;
  documentName?: string;
}

export interface BankAndPspInfoState {
  bankId?: string;
  generalInfo?: PspPartnerItem;
  documents?: Document[];
  accounts?: PspAccountItem[];
  accountsPagination?: Pagination;
  balances?: Balance[];
  submitSuccess?: boolean;
  validators?: PspAccountsValidator;
  pspIntegrations?: PspIntegration[];
  takeCount?: number;
  accountsTakeCount?: number;
  paymentsSettings?: PaymentsSettings[];
  externalAccounts?: ExternalAccount[];
  externalAccount?: ExternalAccount;
  pagination?: Pagination;
  casp?: CASP[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  integrationLogs?: IntegrationLog[];
  integrationLogsPagination?: Pagination;
  integrationLog?: IntegrationLog;
  chains?: { caspId: string; chain: string; id: string }[];
  chainsList?: string[];
  openPayNotificationVisible?: boolean;
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
  externalChangeLogs?: ChangeLog[];
  externalChangeLogsPagination?: Pagination;
  pspAccountsCategories?: PspAccountCategory[];
  feeTiers?: TransactionFeeSettings[];
  linkedClients?: PspPartnersLinkedClient[];
  linkedClientsPagination?: Pagination;
  linkedClientsTakeCount?: number;
}

const initialState: BankAndPspInfoState = {};

export const bankAndPspInfoSlice = createSlice({
  name: 'bankAndPspInfo',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    setLinkedClientsTakeCount: (state, action: PayloadAction<number>) => {
      state.linkedClientsTakeCount = action.payload;
    },
    clearIntegrationLog: (state) => {
      state.integrationLog = undefined;
    },
    setBalances: (state, action: PayloadAction<Balance[]>) => {
      state.balances = action.payload;
    },
    setBankId: (state, action: PayloadAction<string>) => {
      state.bankId = action.payload;
    },
    setChains: (
      state,
      action: PayloadAction<{ caspId: string; chain: string; id: string }[]>,
    ) => {
      state.chains = action.payload;
    },
    setAccountsTakeCount: (state, action: PayloadAction<number>) => {
      state.accountsTakeCount = action.payload;
    },
    setOpenPayNotificationVisible: (state, action: PayloadAction<boolean>) => {
      state.openPayNotificationVisible = action.payload;
    },
    clearExternalAccountsState: (state) => {
      state.externalAccounts = undefined;
      state.takeCount = undefined;
      state.pagination = undefined;
    },
    clearLinkedClientsState: (state) => {
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
      state.linkedClientsTakeCount = undefined;
    },
    clearBankAndPspInfoState: (state) => {
      state.generalInfo = undefined;
      state.documents = undefined;
      state.accounts = undefined;
      state.accountsPagination = undefined;
      state.bankId = undefined;
      state.balances = undefined;
      state.externalAccounts = undefined;
      state.pagination = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.integrationLogs = undefined;
      state.integrationLogsPagination = undefined;
      state.chains = undefined;
      state.feeTiers = undefined;
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
    },
    clearAccountsListState: (state) => {
      state.accounts = undefined;
      state.accountsPagination = undefined;
      state.accountsTakeCount = undefined;
    },
    clearExternalAccountState: (state) => {
      state.externalAccount = undefined;
      state.externalAccountTopUpOptions = undefined;
      state.externalChangeLogs = undefined;
      state.externalChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      pspApi.endpoints.getPspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspIntegrationExternalChains.matchFulfilled,
      (state, action: PayloadAction<{ data: string[] }>) => {
        state.chainsList = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspIntegrationFeeTierList.matchFulfilled,
      (state, action: PayloadAction<Response<TransactionFeeSettings>>) => {
        state.feeTiers = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.createPspIntegrationFeeTierList.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionFeeSettings }>) => {
        let nextFeeTiers: TransactionFeeSettings[] = [];

        if (state.feeTiers?.length) {
          nextFeeTiers = ([] as TransactionFeeSettings[]).concat(
            state.feeTiers,
          );
        }

        nextFeeTiers.push(action.payload.data);

        state.feeTiers = nextFeeTiers;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspIntegrationFeeTierList.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionFeeSettings }>) => {
        const nextFeeTiers = state.feeTiers?.map((item) => {
          let result = { ...item };

          if (item.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });

        state.feeTiers = nextFeeTiers;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.deletePspIntegrationFeeTierList.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionFeeSettings }>) => {
        state.feeTiers = state.feeTiers?.filter(
          (item) => item.id !== action.payload.data.id,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspExternalAccountChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.externalChangeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.externalChangeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.externalChangeLogs = nextChangeLogs;
        }

        state.externalChangeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspExternalAccountTopUpOptions.matchFulfilled,
      (state, action: PayloadAction<Response<ExternalAccountTopUpOption>>) => {
        state.externalAccountTopUpOptions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.deletePSPPaymentAccounts.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccountTopUpOption }>) => {
        state.externalAccountTopUpOptions =
          state?.externalAccountTopUpOptions?.filter(
            (item) => item.id !== action.payload?.data?.id,
          );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccountsCategories.matchFulfilled,
      (state, action: PayloadAction<Response<PspAccountCategory>>) => {
        state.pspAccountsCategories = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updateExternalVaultSettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PspIntegration }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          pspIntegration: action.payload.data,
        } as PspPartnerItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        state.accounts = state.accounts?.map((item) => {
          const result = { ...item };

          if (
            item.id === action?.payload?.data?.id &&
            !isEqual(
              {
                pspIntegrationAccountStatus: item.pspIntegrationAccountStatus,
                realNumberOrIban: item.realNumberOrIban,
                visibleToClient: item.visibleToClient,
              },
              {
                pspIntegrationAccountStatus:
                  action?.payload?.data?.pspIntegrationAccountStatus,
                realNumberOrIban: action?.payload?.data?.realNumberOrIban,
                visibleToClient: action?.payload?.data?.visibleToClient,
              },
            )
          ) {
            result.pspIntegrationAccountStatus =
              action.payload.data.pspIntegrationAccountStatus;
            result.realNumberOrIban = action.payload.data.realNumberOrIban;
            result.visibleToClient = action.payload.data.visibleToClient;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspIntegrationsLogs.matchFulfilled,
      (state, action: PayloadAction<Response<IntegrationLog>>) => {
        state.integrationLogs = action.payload.data?.edges?.map((item) => ({
          ...item.node,
          urlList: generatePath(BankRoutes.BANK_AND_PSP_INTEGRATION_LOGS_ITEM, {
            bankId: state.bankId as string,
            logId: item.node.id,
          }),
          types: item?.node?.type,
          transactionIds: item?.node?.transactionId,
        }));

        state.integrationLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getCaspChains.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{ caspId: string; id: string; chain: string }>
        >,
      ) => {
        state.chains = action.payload?.data?.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          ...action.payload.data,
        };
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updateVisibleTocClients.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          ...action.payload.data,
        };
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        if (state.accounts) {
          const nextAccounts = state.accounts.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...result,
                ...action.payload.data,
              };
            }

            return result;
          });

          state.accounts = nextAccounts;
        }
      },
    );
    builder.addMatcher(
      pspApi.endpoints.enablePspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        if (state.accounts) {
          const nextAccounts = state.accounts.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...result,
                ...action.payload.data,
              };
            }

            return result;
          });

          state.accounts = nextAccounts;
        }
      },
    );
    builder.addMatcher(
      pspApi.endpoints.enablePspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          active: action.payload.data.active,
        } as PspPartnerItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getBankChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.disablePspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          active: action.payload.data.active,
        } as PspPartnerItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccountsValidators.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountsValidator }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getConfigurationPspAccounts.matchFulfilled,
      (state, action: PayloadAction<Response<PspAccountItem>>) => {
        state.accounts = action.payload.data.edges.map((item) => ({
          ...item.node,
          balance: '-',
          fullNumber: '-',
          overdraftEnabled: item.node?.account?.overdraftEnabled,
          type: item.node.type || '',
        }));

        state.accountsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspPartnerDocuments.matchFulfilled,
      (state, action: PayloadAction<Response<Document>>) => {
        state.documents = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspPartnerLinkedClients.matchFulfilled,
      (state, action: PayloadAction<Response<PspPartnersLinkedClient>>) => {
        state.linkedClients = action.payload.data.edges.map(
          (item) => item.node,
        );
        state.linkedClientsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPSPExternalAccountsList.matchFulfilled,
      (state, action: PayloadAction<Response<ExternalAccount>>) => {
        state.externalAccounts = action?.payload?.data?.edges?.map((item) => ({
          pspPartnerId: '-',
          currencyId: '-',
          paymentType: '-',
          accountId: '-',
          accountName: '-',
          accountType: '-',
          active: false,
          url: generatePath(BankRoutes.BANK_AND_PSP_EXTERNAL_ITEM, {
            bankId: state.bankId || '',
            accountId: item.node.id,
          }),
          ...item.node,
        }));
        state.pagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPSPExternalAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccount = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspExternalAccountCurrencies.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.currenciesIds = action.payload.data.currenciesIds;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getBankCASP.matchFulfilled,
      (state, action: PayloadAction<Response<CASP>>) => {
        state.casp = action?.payload?.data?.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.addPartnerDocuments.matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        const nextDocuments = ([] as Document[]).concat(
          state.documents as Document[],
        );

        nextDocuments.push(action.payload.data);

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.deletePspPartnerDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: { id: string } }>) => {
        const nextDocuments = state?.documents?.filter(
          (item) => item.id !== action.payload.data.id,
        );

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspIntegrationsLogsById.matchFulfilled,
      (state, action: PayloadAction<{ data: IntegrationLog }>) => {
        state.integrationLog = {
          ...action.payload.data,
          types: action.payload.data.type,
        };
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspIntegrations.matchFulfilled,
      (state, action: PayloadAction<Response<PspIntegration>>) => {
        state.pspIntegrations = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updateExchangeToleranceSettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PspIntegration }>) => {
        state.pspIntegrations = state.pspIntegrations?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.hasExchangeRateTolerance =
              action?.payload?.data?.hasExchangeRateTolerance;
            result.exchangeRateTolerance =
              action?.payload?.data?.exchangeRateTolerance;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspPaymentsSetting.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentsSettings>>) => {
        state.paymentsSettings = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.addPspPaymentsSettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentsSettings }>) => {
        let nextPaymentSettings: PaymentsSettings[] = [];

        if (state?.paymentsSettings && state?.paymentsSettings?.length > 0) {
          nextPaymentSettings = ([] as PaymentsSettings[]).concat(
            state.paymentsSettings,
          );
        }

        nextPaymentSettings.push(action.payload.data);

        state.paymentsSettings = nextPaymentSettings;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspPaymentsSettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentsSettings }>) => {
        const nextPaymentSettings = state.paymentsSettings?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });

        state.paymentsSettings = nextPaymentSettings;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.applyPspPartnerIntegration.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          pspIntegration: action.payload.data.pspIntegration,
          pspIntegrationId: action.payload.data.pspIntegrationId,
        } as PspPartnerItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspExternalAccountStatus.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                active: boolean;
                ids: string[];
              };
            };
          };
        },
      ) => {
        const nextExternalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action?.meta.arg?.originalArgs?.ids[0]) {
            result.active = action?.meta.arg?.originalArgs.active;
          }

          return result;
        });

        state.externalAccounts = nextExternalAccounts;
      },
    );
  },
});

export const {
  setBalances,
  clearBankAndPspInfoState,
  setTakeCount,
  setBankId,
  clearIntegrationLog,
  setChains,
  clearExternalAccountState,
  setAccountsTakeCount,
  setOpenPayNotificationVisible,
  setLinkedClientsTakeCount,
  clearExternalAccountsState,
  clearLinkedClientsState,
  clearAccountsListState,
} = bankAndPspInfoSlice.actions;

export default bankAndPspInfoSlice.reducer;
